import {
  Business,
  Close,
  PersonSearch,
  SupervisorAccountOutlined,
} from "@mui/icons-material";
import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo } from "react";

export type BaseSearchOption = {
  id?: string;
  name?: string;
  name_secondary?: string;
  type?: string;
  icon?: React.ElementType;
  data?: { id?: string };
};

export type DefaultSearchListProps<SearchOption extends BaseSearchOption> = {
  options: SearchOption[];
  selected: SearchOption | undefined;
  onSelect: (option?: SearchOption | undefined) => void;
  disabled?: boolean;
};

//TODO: make DefaultSearchList less attached to the accounts directory and make a separate accounts search list
function DefaultSearchList<SearchOption extends BaseSearchOption>({
  options,
  selected,
  onSelect,
  disabled,
}: DefaultSearchListProps<SearchOption>) {
  const content = useMemo(() => {
    const getOptionIcon = (option: SearchOption) => {
      if (option.icon) {
        return <option.icon />;
      } else if (option.type === "organization") {
        return (
          <Tooltip title="Organization">
            <Business />
          </Tooltip>
        );
      } else if (option.type === "member") {
        return (
          <Tooltip title="Member">
            <PersonSearch />
          </Tooltip>
        );
      } else {
        return (
          <Tooltip title="Account">
            <SupervisorAccountOutlined />
          </Tooltip>
        );
      }
    };

    return options.map((option) => {
      const isSelected = selected?.data?.id === option.data?.id;

      if (!isSelected) {
        return (
          <ListItem disableGutters disablePadding key={option.data?.id}>
            <ListItemButton
              onClick={() => onSelect(option)}
              disabled={disabled}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.03)",
                  borderRadius: "8px",
                },
              }}
            >
              <ListItemIcon>{getOptionIcon(option)}</ListItemIcon>
              <ListItemText
                sx={{
                  color: option.type === "external" ? "grey" : "unset",
                }}
              >
                <Typography>
                  {option.name}{" "}
                  <span style={{ color: "grey" }}>
                    {option.name_secondary && `- ${option.name_secondary}`}
                  </span>
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        );
      } else {
        return (
          <ListItem
            sx={{
              bgcolor: "rgba(0, 0, 0, 0.03)",
            }}
            secondaryAction={
              <IconButton
                onClick={() => onSelect(undefined)}
                size="small"
                disabled={disabled}
              >
                <Close fontSize="small" />
              </IconButton>
            }
            key={selected?.id || ""}
          >
            <ListItemIcon>{selected && getOptionIcon(selected)}</ListItemIcon>
            <ListItemText
              sx={{
                color: option.type === "external" ? "grey" : "unset",
              }}
            >
              <Typography>
                {option.name}{" "}
                <span style={{ color: "grey" }}>
                  {option.name_secondary && `- ${option.name_secondary}`}
                </span>
              </Typography>
            </ListItemText>
          </ListItem>
        );
      }
    });
  }, [disabled, onSelect, options, selected]);

  return (
    <List
      sx={{
        bgcolor: "background.paper",
        borderRadius: "8px",
        py: 1,
        px: 1,
        maxHeight: "40vh",
        overflowY: "auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        "& .MuiListItem-root": {
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.1)",
          borderRadius: "8px",
        },
      }}
    >
      {content}
    </List>
  );
}

export default DefaultSearchList;
